import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight } from "@phosphor-icons/react";
import { useSeo } from "../lib/seo";
import { HERO_IMAGE, FEATURED_PROJECTS, PRESS_OUTLETS } from "../lib/data";

const buildPersonSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Shaji Nada",
  url: typeof window !== "undefined" ? `${window.location.origin}/` : "/",
  jobTitle: "Film Producer, Music Executive",
  worksFor: [
    { "@type": "Organization", name: "Ideal Entertainment" },
    { "@type": "Organization", name: "Maajja" },
  ],
  affiliation: ["Ideal Entertainment", "Maajja", "Ideal Group"],
  nationality: "Canadian",
  address: { "@type": "PostalAddress", addressLocality: "Toronto", addressCountry: "Canada" },
  sameAs: [
    "https://www.linkedin.com/",
    "https://www.imdb.com/",
    "https://www.crunchbase.com/",
    "https://x.com/",
    "https://www.instagram.com/",
    "https://www.youtube.com/",
  ],
  description:
    "Toronto-based film producer and music executive. Founder of Ideal Entertainment and co-founder of Maajja with A.R. Rahman, championing South Asian voices globally.",
});

export default function Home() {
  useSeo({
    title: "Shaji Nada | Producer & Founder of Maajja and Ideal Entertainment",
    description:
      "Shaji Nada — Toronto-based film producer and music executive. Founder of Ideal Entertainment, co-founder of Maajja with A.R. Rahman.",
    canonicalPath: "/",
    jsonLd: buildPersonSchema(),
  });

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[100svh] min-h-[640px] w-full overflow-hidden grain" data-testid="home-hero">
        <img
          src={HERO_IMAGE}
          alt="Shaji Nada — film producer and music executive, editorial portrait"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "65% 35%" }}
          loading="eager"
          fetchPriority="high"
        />
        {/* Dark cinematic overlays — gentler since source photo is already moody dark */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-transparent" />

        <div className="relative z-10 h-full max-w-[1600px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-20 md:pb-32">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="overline mb-6"
            data-testid="hero-overline"
          >
            Toronto · Producer · Founder
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="font-serif-display text-5xl sm:text-6xl md:text-7xl lg:text-[7.5rem] text-white leading-[0.96] tracking-tight max-w-5xl"
            data-testid="hero-headline"
          >
            Shaji Nada — <span className="italic font-light text-[var(--accent)]">Producer.</span> Founder. Storyteller.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.55 }}
            className="mt-8 text-lg md:text-xl text-white/70 font-light max-w-2xl leading-relaxed"
            data-testid="hero-subhead"
          >
            Championing South Asian voices on the global stage — across independent music, cinema, and culture.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75 }}
            className="mt-12 flex flex-wrap gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-contact"
              className="group inline-flex items-center gap-3 px-7 py-4 border border-[var(--accent)] text-[var(--accent)] uppercase tracking-[0.2em] text-xs font-medium hover:bg-[var(--accent)] hover:text-black transition-colors duration-300"
            >
              Get in touch
              <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/ventures"
              data-testid="hero-cta-ventures"
              className="inline-flex items-center gap-3 px-7 py-4 border border-white/20 text-white uppercase tracking-[0.2em] text-xs font-medium hover:border-white hover:bg-white hover:text-black transition-colors duration-300"
            >
              View ventures
            </Link>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-6 right-6 md:right-12 z-10 hidden md:flex flex-col items-center gap-3 text-white/40">
          <span className="text-[10px] uppercase tracking-[0.3em] [writing-mode:vertical-rl] rotate-180">Scroll</span>
          <div className="w-px h-12 bg-white/30" />
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="relative py-24 md:py-40 px-6 md:px-12" data-testid="home-projects">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
            <div>
              <p className="overline mb-5">Selected Work</p>
              <h2 className="font-serif-display text-4xl md:text-6xl text-white leading-[1.0] max-w-2xl">
                A decade producing across <span className="italic text-[var(--accent)]">music and film.</span>
              </h2>
            </div>
            <Link to="/films" data-testid="projects-view-all" className="link-underline text-white/70 uppercase text-xs tracking-[0.22em]">
              View all →
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="projects-grid">
            {FEATURED_PROJECTS.map((p, idx) => (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: idx * 0.08 }}
                className="group flex flex-col"
                data-testid={`project-card-${idx}`}
              >
                <Link to={p.href} className="block">
                  {/* Equal-height image frame: every image uses object-contain with breathing room. */}
                  <div
                    className={`relative aspect-[4/3] overflow-hidden flex items-center justify-center p-6 md:p-8 border transition-all duration-500 ease-out ${
                      p.lightBg
                        ? "bg-white border-white/10 group-hover:border-[var(--accent)]/50"
                        : "bg-[#141414] border-white/10 group-hover:bg-[#1A1A1A] group-hover:border-[var(--accent)]/50"
                    }`}
                  >
                    <img
                      src={p.image}
                      alt={`${p.name} — ${p.tagline}, Shaji Nada`}
                      loading="lazy"
                      className="max-w-full max-h-full w-auto h-auto object-contain transition-transform duration-700 ease-out group-hover:scale-[1.035]"
                    />
                    {/* Subtle accent glow on hover */}
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      style={{
                        background:
                          "radial-gradient(circle at 50% 100%, rgba(206,25,25,0.10) 0%, transparent 60%)",
                      }}
                    />
                  </div>

                  {/* Caption sits below the frame so it never crops the image */}
                  <div className="pt-5 flex items-baseline justify-between gap-4">
                    <div>
                      <p className="overline">{p.tagline}</p>
                      <h3 className="font-serif-display text-2xl md:text-3xl text-white mt-2 group-hover:text-[var(--accent)] transition-colors duration-400">
                        {p.name}
                      </h3>
                    </div>
                    <span
                      aria-hidden
                      className="text-white/30 text-xs uppercase tracking-[0.22em] transition-all duration-400 group-hover:text-[var(--accent)] group-hover:translate-x-1"
                    >
                      →
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRESS MARQUEE */}
      <section className="py-20 border-y border-white/5 bg-[#101010]" data-testid="home-press-strip">
        <p className="overline text-center mb-8">Selected Press</p>
        <Marquee gradient gradientColor="#101010" gradientWidth={120} speed={28} pauseOnHover>
          {PRESS_OUTLETS.concat(PRESS_OUTLETS).map((outlet, i) => (
            <span
              key={`${outlet}-${i}`}
              className="font-serif-display italic text-3xl md:text-4xl text-white/40 mx-12 whitespace-nowrap hover:text-[var(--accent)] transition-colors"
            >
              {outlet}
            </span>
          ))}
        </Marquee>
      </section>

      {/* CTA STRIP */}
      <section className="px-6 md:px-12 py-32 md:py-48" data-testid="home-cta">
        <div className="max-w-[1600px] mx-auto grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-8">
            <p className="overline mb-6">Conversations welcome</p>
            <h2 className="font-serif-display text-5xl md:text-7xl text-white leading-[1.02]">
              Press, partners, and <br className="hidden md:block" />
              <span className="italic text-[var(--accent)]">artists welcome.</span>
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/contact"
              data-testid="cta-contact-bottom"
              className="inline-flex items-center gap-3 px-8 py-5 border border-[var(--accent)] text-[var(--accent)] uppercase tracking-[0.22em] text-xs font-medium hover:bg-[var(--accent)] hover:text-black transition-colors duration-300"
            >
              Start a conversation
              <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
